/**
 * Bulk Inspection Constants & Helpers
 *
 * Pure data + helper functions (zero React). Shared between grid and review steps.
 * Scoring: 150 points max. Each item starts at max penalty, reduced by better reading.
 */

// ---------------------------------------------------------------------------
// Resource columns — grouped by inspection category
// ---------------------------------------------------------------------------

export const RESOURCE_COLUMNS = [
  // Tires
  { key: 'tire_front_left',  label: 'Pneu AV gauche', shortLabel: 'AVG', group: 'tires' },
  { key: 'tire_front_right', label: 'Pneu AV droit',  shortLabel: 'AVD', group: 'tires' },
  { key: 'tire_rear_left',   label: 'Pneu AR gauche', shortLabel: 'ARG', group: 'tires' },
  { key: 'tire_rear_right',  label: 'Pneu AR droit',  shortLabel: 'ARD', group: 'tires' },
  // Brakes
  { key: 'brake_pad_front',  label: 'Plaquettes AV',  shortLabel: 'Frein AV', group: 'brakes' },
  { key: 'brake_pad_rear',   label: 'Plaquettes AR',  shortLabel: 'Frein AR', group: 'brakes' },
  // Fluids
  { key: 'fuel_level',       label: 'Carburant',      shortLabel: 'Carb.',    group: 'fluids' },
  { key: 'adblue_level',     label: 'AdBlue',         shortLabel: 'AdBlue',   group: 'fluids', dieselOnly: true },
  { key: 'oil_level',        label: 'Huile moteur',   shortLabel: 'Huile',    group: 'fluids' },
  { key: 'brake_fluid',      label: 'Liquide frein',  shortLabel: 'Liq.frein',group: 'fluids' },
  { key: 'coolant_level',    label: 'Refroidissement',shortLabel: 'Refroid.', group: 'fluids' },
  { key: 'washer_fluid',     label: 'Lave-glace',     shortLabel: 'L-glace',  group: 'fluids' },
  // Hull
  { key: 'windshield',       label: 'Pare-brise',     shortLabel: 'P-brise',  group: 'hull' },
  { key: 'body_condition',   label: 'Carrosserie',    shortLabel: 'Carross.', group: 'hull' },
  { key: 'headlights',       label: 'Eclairage AV',   shortLabel: 'Ecl. AV',  group: 'hull' },
  { key: 'taillights',       label: 'Eclairage AR',   shortLabel: 'Ecl. AR',  group: 'hull' },
  { key: 'mirrors',          label: 'Retroviseurs',   shortLabel: 'Retro.',   group: 'hull' },
];

export const ALL_GROUPS = ['tires', 'brakes', 'fluids', 'hull'];

export const GROUP_LABELS = {
  tires:  'Pneus',
  brakes: 'Freins',
  fluids: 'Fluides',
  hull:   'Carrosserie',
};

export const GROUP_ICONS = {
  tires:  'Circle',
  brakes: 'Disc',
  fluids: 'Droplet',
  hull:   'Shield',
};

// ---------------------------------------------------------------------------
// Range values — 4 levels, sent as percent to API
// ---------------------------------------------------------------------------

// Index: 0=critical, 1=warning, 2=acceptable, 3=good
export const RANGE_VALUES  = [10, 35, 65, 100];
export const RANGE_GLYPHS = ['\u2717', '!', '~', '\u2713'];  // ✗ ! ~ ✓
export const RANGE_LABELS = ['Critique', 'Attention', 'Acceptable', 'Bon'];

export const RANGE_COLORS = {
  bg:   ['bg-red-500',    'bg-orange-400',  'bg-yellow-400',  'bg-green-500'],
  text: ['text-red-600',  'text-orange-600','text-yellow-600','text-green-600'],
  ring: ['ring-red-500',  'ring-orange-400','ring-yellow-400','ring-green-500'],
  light:['bg-red-100',    'bg-orange-100',  'bg-yellow-100',  'bg-green-100'],
};

// ---------------------------------------------------------------------------
// Scoring system — 150 points max
// ---------------------------------------------------------------------------

export const VEHICLE_MAX_SCORE = 150;

// Penalties per reading: index 0=critical -> 15pts, 3=good -> 0pts
export const PENALTIES = [15, 10, 5, 0];

// Driver-managed resources (excluded from inspection scoring — fuel level depends on driving, not vehicle condition)
export const DRIVER_RESOURCES = new Set(['fuel_level', 'adblue_level']);

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Check if a column applies to a vehicle (handles diesel-only) */
export function columnApplies(col, vehicle) {
  if (col.dieselOnly && vehicle.fuel_type !== 'diesel') return false;
  return true;
}

/** Get all columns for a specific group */
export function getGroupColumns(group) {
  return RESOURCE_COLUMNS.filter(c => c.group === group);
}

/** Get all columns applicable to a vehicle */
export function getApplicableColumns(vehicle) {
  return RESOURCE_COLUMNS.filter(c => columnApplies(c, vehicle));
}

/** Get scorable columns (exclude driver-managed) */
export function getScorableColumns(vehicle) {
  return getApplicableColumns(vehicle).filter(c => !DRIVER_RESOURCES.has(c.key));
}

/** Worst reading index in a group for a vehicle (null if no readings) */
export function worstInGroup(group, vehicleId, readings) {
  const vr = readings[vehicleId] || {};
  const cols = getGroupColumns(group);
  let worst = null;
  for (const col of cols) {
    const val = vr[col.key];
    if (val !== null && val !== undefined) {
      if (worst === null || val < worst) worst = val;
    }
  }
  return worst;
}

/** Compute vehicle score out of VEHICLE_MAX_SCORE */
export function computeVehicleScore(vehicleId, readings, vehicle) {
  const vr = readings[vehicleId] || {};
  const scorable = getScorableColumns(vehicle);
  // Max possible = scorable.length * PENALTIES[0] (but capped at VEHICLE_MAX_SCORE)
  const maxPenalty = scorable.length * PENALTIES[0];
  let totalPenalty = 0;
  let measured = 0;

  for (const col of scorable) {
    const val = vr[col.key];
    if (val !== null && val !== undefined) {
      totalPenalty += PENALTIES[val];
      measured++;
    } else {
      // Unmeasured = assume worst
      totalPenalty += PENALTIES[0];
    }
  }

  if (maxPenalty === 0) return VEHICLE_MAX_SCORE;
  const score = Math.round(VEHICLE_MAX_SCORE * (1 - totalPenalty / maxPenalty));
  return { score, measured, total: scorable.length };
}

/** Compute fleet average score */
export function computeFleetScore(vehicleIds, readings, vehiclesMap) {
  if (vehicleIds.length === 0) return 0;
  let sum = 0;
  for (const id of vehicleIds) {
    const result = computeVehicleScore(id, readings, vehiclesMap[id] || {});
    sum += (typeof result === 'number' ? result : result.score);
  }
  return Math.round(sum / vehicleIds.length);
}

/** Score threshold for color coding */
export function scoreThreshold(score) {
  if (score >= 120) return { level: 'good',    color: 'text-green-600',  bg: 'bg-green-500',  stroke: '#10B981' };
  if (score >= 90)  return { level: 'ok',      color: 'text-yellow-600', bg: 'bg-yellow-500', stroke: '#F59E0B' };
  if (score >= 60)  return { level: 'warning', color: 'text-orange-600', bg: 'bg-orange-500', stroke: '#F97316' };
  return               { level: 'critical',color: 'text-red-600',    bg: 'bg-red-500',    stroke: '#EF4444' };
}

/** Action suggestion based on reading index */
export function actionForReading(readingIdx) {
  if (readingIdx === 0) return { action: 'Maintenance urgente', severity: 'critical', color: 'text-red-600' };
  if (readingIdx === 1) return { action: 'Maintenance planifiee', severity: 'warning', color: 'text-orange-600' };
  if (readingIdx === 2) return { action: 'A surveiller', severity: 'watch', color: 'text-yellow-600' };
  return { action: 'OK', severity: 'ok', color: 'text-green-600' };
}

/** Verdict info for a vehicle score */
export function verdictInfo(score) {
  if (score >= 120) return { label: 'Conforme',  emoji: '', color: 'text-green-600',  bg: 'bg-green-100',  border: 'border-green-400' };
  if (score >= 90)  return { label: 'Acceptable', emoji: '', color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-400' };
  if (score >= 60)  return { label: 'A surveiller',emoji: '', color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-400' };
  return                   { label: 'Non conforme',emoji: '', color: 'text-red-600',    bg: 'bg-red-100',    border: 'border-red-400' };
}

/** Group completion status for LED indicators */
export function groupStatus(group, vehicleIds, readings) {
  const cols = getGroupColumns(group);
  let filled = 0;
  let total = 0;
  let worst = null;

  for (const vid of vehicleIds) {
    const vr = readings[vid] || {};
    for (const col of cols) {
      total++;
      const val = vr[col.key];
      if (val !== null && val !== undefined) {
        filled++;
        if (worst === null || val < worst) worst = val;
      }
    }
  }

  if (filled === 0) return { status: 'empty', color: 'bg-gray-300', worst: null };
  if (filled < total) return { status: 'partial', color: RANGE_COLORS.bg[worst] + '/60', worst };
  return { status: 'complete', color: RANGE_COLORS.bg[worst], worst };
}
